import React, { FunctionComponent, memo, useCallback } from "react";
import { Grid, Theme, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Input } from "@shared/components";
import { isValidEmail, isValidName } from "@requestContactUpdate/utils/validation";

interface Props {
  contactName: string;
  contactEmail: string;
  onContactNameChange(value: string): void;
  onContactEmailChange(value: string): void;
  contactNameError?: boolean;
  contactEmailError?: boolean;
  contactNameExceedsMax?: boolean;
  contactEmailExceedsMax?: boolean;
  maxContactNameLength?: number;
  maxContactEmailLength?: number;
  showErrors?: boolean;
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: "flex",
    gap: theme.spacing(7),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: theme.spacing(4),
    },
  },
  inputWrapper: {
    flex: 1,
    "& input": {
      marginLeft: "20px",
    },
  },
}));

const RequestContactUpdateInput: FunctionComponent<Props> = ({
  contactName,
  contactEmail,
  onContactNameChange,
  onContactEmailChange,
  contactNameError,
  contactEmailError,
  contactNameExceedsMax,
  contactEmailExceedsMax,
  maxContactNameLength,
  maxContactEmailLength,
  showErrors = false,
}) => {
  const classes = useStyles();

  const handleContactNameChange = useCallback(
    (value: string) => {
      onContactNameChange(value);
    },
    [onContactNameChange]
  );

  const handleContactEmailChange = useCallback(
    (value: string) => {
      onContactEmailChange(value);
    },
    [onContactEmailChange]
  );

  return (
    <Grid item xs={12}>
      <div className={classes.container}>
        <div className={classes.inputWrapper}>
          <Input
            placeholder="Contact Name"
            value={contactName}
            onChange={handleContactNameChange}
            error={contactNameError || contactNameExceedsMax}
            size="large"
            variant="contained"
            testId="request-contact-update-name-input"
          />
          {showErrors && !contactName.trim() && (
            <Box mt={1} color="warning.main" fontSize={12}>
              Contact name is required
            </Box>
          )}
          {showErrors && contactName.trim() && !isValidName(contactName) && (
            <Box mt={1} color="warning.main" fontSize={12}>
              Invalid contact name
            </Box>
          )}
          {contactNameExceedsMax && (
            <Box mt={1} color="warning.main" fontSize={12}>
              Max {maxContactNameLength} characters
            </Box>
          )}
        </div>
        <div className={classes.inputWrapper}>
          <Input
            placeholder="Contact Email"
            value={contactEmail}
            onChange={handleContactEmailChange}
            error={contactEmailError || contactEmailExceedsMax}
            size="large"
            variant="contained"
            testId="request-contact-update-email-input"
          />
          {showErrors && !contactEmail.trim() && (
            <Box mt={1} color="warning.main" fontSize={12}>
              Contact email is required
            </Box>
          )}
          {showErrors && contactEmail.trim() && !isValidEmail(contactEmail) && (
            <Box mt={1} color="warning.main" fontSize={12}>
              Invalid contact email
            </Box>
          )}
          {contactEmailExceedsMax && (
            <Box mt={1} color="warning.main" fontSize={12}>
              Max {maxContactEmailLength} characters
            </Box>
          )}
        </div>
      </div>
    </Grid>
  );
};

export default memo<Props>(RequestContactUpdateInput);
